import { Input, Select } from 'alurkerja-ui'
import { Popover } from 'react-tiny-popover'
import { useUiFromSpecContext } from '../../EditorContextProvider'
import { FaPaintbrush } from 'react-icons/fa6'
import { FaBold, FaItalic, FaUnderline } from 'react-icons/fa'
import { RxTextAlignLeft, RxTextAlignRight, RxTextAlignCenter, RxTextAlignJustify } from 'react-icons/rx'
import { ColorPicker } from 'react-pick-color'
import clsx from 'clsx'
import { useState } from 'react'
import { useTranslation } from '../../hooks/useTranslation'

export default function DesignPanel() {
  const { designState, setDesignState } = useUiFromSpecContext()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isPopoverTextOpen, setIsPopoverTextOpen] = useState(false)
  const { t } = useTranslation()

  const labelFormats = [
    {
      label: <FaBold />,
      value: 'bold',
    },
    {
      label: <FaItalic />,
      value: 'italic',
    },
    {
      label: <FaUnderline />,
      value: 'underline',
    },
  ]
  const labelAlign = [
    {
      label: <RxTextAlignLeft />,
      value: 'left',
    },
    {
      label: <RxTextAlignCenter />,
      value: 'center',
    },
    {
      label: <RxTextAlignRight />,
      value: 'right',
    },
    {
      label: <RxTextAlignJustify />,
      value: 'justify',
    },
  ]

  return (
    <div className="flex flex-col space-y-4 p-1 h-full">
      <div className="gap-4">
        <h3 className="text-md font-semibold flex items-center gap-2">
          <FaPaintbrush />
          {t('designPanel.title')}
        </h3>
      </div>
      <div className="flex-col flex gap-2">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600">{t('designPanel.fontFamily')}</label>
          <Select
            options={options}
            isSearchable={false}
            classNames={{
              control: () =>
                'h-8 text-xs flex rounded-md border border-[#c4c4c480] py-2 px-3 bg-white focus:ring-indigo-600 focus-within:ring-indigo-600 focus-within:border-indigo-600 focus:border-indigo-600',
              option: () => '!text-xs',
            }}
            value={{
              value: designState.fontFamily ?? 'sans',
              label: options.find((opt) => opt.value === designState.fontFamily)?.label ?? 'Poppins',
            }}
            defaultValue={{
              value: designState.fontFamily ?? 'sans',
              label: options.find((opt) => opt.value === designState.fontFamily)?.label ?? 'Poppins',
            }}
            onChange={(e) => {
              setDesignState({
                ...designState,
                fontFamily: (e as { value: string }).value,
              })
            }}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">{t('designPanel.fontSize')}</label>
            <Input
              type="number"
              value={designState.fontSize ?? 16}
              min={8}
              max={72}
              step={2}
              onChange={(e) => {
                setDesignState({
                  ...designState,
                  fontSize: parseInt(e.target.value, 10),
                })
              }}
              className="h-8 text-xs flex rounded-md border border-[#c4c4c480] py-2 px-3 bg-white focus:ring-indigo-600 focus-within:ring-indigo-600 focus-within:border-indigo-600 focus:border-indigo-600"
              placeholder="Enter font size in px"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">{t('designPanel.labelFormat')}</label>
            <div className="flex bg-slate-100 border rounded-md overflow-hidden">
              {labelFormats.map((format) => (
                <button
                  key={format.value}
                  onClick={() => {
                    const currentFormats = designState.textFormat || []
                    const formatValue = format.value as 'bold' | 'italic' | 'underline'
                    setDesignState({
                      ...designState,
                      textFormat: currentFormats.includes(formatValue)
                        ? currentFormats.filter((item) => item !== formatValue)
                        : [...currentFormats, formatValue],
                    })
                  }}
                  className={clsx('flex items-center justify-center w-full h-8 text-xs bg-white hover:bg-slate-50', {
                    '!bg-blue-50 text-main-blue-alurkerja': (designState.textFormat || []).includes(
                      format.value as 'bold' | 'italic' | 'underline'
                    ),
                  })}
                >
                  {format.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">{t('designPanel.labelAllignment')}</label>
            <div className="flex bg-slate-100 border rounded-md overflow-hidden">
              {labelAlign.map((format) => (
                <button
                  key={format.value}
                  onClick={() => {
                    setDesignState({
                      ...designState,
                      textAlign: format.value as 'left' | 'center' | 'right' | 'justify',
                    })
                  }}
                  className={clsx('flex items-center justify-center w-full h-8 text-md bg-white hover:bg-slate-50', {
                    '!bg-blue-50 text-main-blue-alurkerja': designState.textAlign === format.value,
                  })}
                >
                  {format.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">{t('designPanel.textColor')}</label>
            <Popover
              isOpen={isPopoverTextOpen}
              positions={['bottom', 'right', 'top']} // preferred positions by priority
              content={
                <ColorPicker
                  color={designState.textColor || '#000000'}
                  presets={['#000000', '#333333', '#666666', '#999999', '#1a1a1a']}
                  onChange={(color) =>
                    setDesignState({
                      ...designState,
                      textColor: color.hex,
                    })
                  }
                />
              }
              onClickOutside={() => setIsPopoverTextOpen(false)}
            >
              <button
                className="w-full h-8 text-xs flex rounded-md border border-[#c4c4c480] py-2 px-3 bg-white focus:ring-indigo-600 focus-within:ring-indigo-600 focus-within:border-indigo-600 focus:border-indigo-600"
                onClick={() => setIsPopoverTextOpen(!isPopoverTextOpen)}
              >
                {designState.textColor || '#000000'}
              </button>
            </Popover>
          </div>
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600">{t('designPanel.backgroundColor')}</label>
          <Popover
            isOpen={isPopoverOpen}
            positions={['bottom', 'right', 'top']} // preferred positions by priority
            content={
              <ColorPicker
                color={designState.backgroundColor || '#ffffff'}
                presets={['#ffffff', '#f0f0f0', '#e0e0e0', '#d0d0d0', '#c0c0c0']}
                onChange={(color) =>
                  setDesignState({
                    ...designState,
                    backgroundColor: color.hex,
                  })
                }
              />
            }
            onClickOutside={() => setIsPopoverOpen(false)}
          >
            <button
              className="w-full h-8 text-xs flex rounded-md border border-[#c4c4c480] py-2 px-3 bg-white focus:ring-indigo-600 focus-within:ring-indigo-600 focus-within:border-indigo-600 focus:border-indigo-600"
              onClick={() => setIsPopoverOpen(!isPopoverOpen)}
            >
              {designState.backgroundColor || '#ffffff'}
            </button>
          </Popover>
        </div>
      </div>
    </div>
  )
}

const options = [
  {
    value: 'sans',
    label: 'Poppins',
  },
  {
    value: 'times-new-roman',
    label: 'Times New Roman',
  },
  {
    value: 'arial',
    label: 'Arial',
  },
  {
    value: 'calibri',
    label: 'Calibri',
  },
  {
    value: 'roboto',
    label: 'Roboto',
  },
  {
    value: 'comforta',
    label: 'Comforta',
  },
  {
    value: 'oswald',
    label: 'Oswald',
  },
  {
    value: 'verdana',
    label: 'Verdana',
  },
  {
    value: 'comic-sans',
    label: 'Comic Sans MS',
  },
  {
    value: 'impact',
    label: 'Impact',
  },
  {
    value: 'lato',
    label: 'Lato',
  },
]
